/* eslint-disable max-lines-per-function */
import {
  Button,
  Input,
  FieldLabel,
  Field,
  FieldError,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import { useDeleteBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useDeleteBelegung';
import { TimeInput } from '@/domain/dienstplan/shared/components/TimeInput/TimeInput';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';
import { getColorsForMarkierung } from '@/domain/dienstplan/shared/utils/getColorForMarkierung';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { toast } from '@/hooks/use-toast';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { CSSVariables, FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { VisualEditorState } from '@/domain/dienstplan/features/VisualEditor/store';
import { formatHourDecimalToTimeString } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/formatHourDecimalToTimeString';
import { transformTimeStringToDecimal } from '@/domain/dienstplan/features/VisualEditor/utils/timeHelper/transformTimeStringToDecimal';
import { useForm } from '@/domain/dienstplan/features/VisualEditor/components/BelegungPopover/useForm';
import { useVisualEditorContext } from '@/domain/dienstplan/features/VisualEditor/VisualEditorContext';
import { MITARBEITER_THRESHOLD } from '@/domain/dienstplan/features/VisualEditor/constants';

export function BelegungPopover({
  triggerStyle,
  popoverRect,
  store,
  rects,
  open,
  setOpen,
  setPopoverPosition,
}: {
  triggerStyle: CSSProperties;
  popoverRect: Belegung;
  store: VisualEditorState;
  rects: BelegungsIntervall[];
  open: boolean;
  setOpen: (state: boolean) => void;
  setPopoverPosition: (styles: CSSProperties | undefined) => void;
}) {
  const { t } = useTranslation();
  const [deleteBelegung] = useDeleteBelegung();
  const { maxMitarbeiterAnzahl } = useVisualEditorContext();

  const offset = store.ui.xAxisOffset;

  const wochentagLabel = getLocaleWeekdayString(popoverRect.tag);

  const form = useForm({ popoverRect, rects, setOpen });

  function handleDelete() {
    deleteBelegung({ belegungId: popoverRect.id }).then((result) => {
      if (result.data.dienstplan_deleteBelegung) {
        store.removeBelegungById(popoverRect.id);
        toast({
          title: t(
            'domain.dienstplan.mindestbelegung.visualEditor.popover.deleteSuccess',
          ),
        });
        setOpen(false);
        setPopoverPosition(undefined);
      }
    });
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild className="react-popover-trigger">
        <div
          style={triggerStyle}
          className="bg-neutrals-100 shadow-lg flex gap-2 rounded-md p-2"
          role="group"
          aria-label="Belegung actions">
          <Button
            variant="icon"
            className=" text-primary-blue-700"
            aria-label="Edit-Popover">
            <FontAwesomeIcon icon={faPenToSquare} />
          </Button>
          <Button
            aria-label="Delete-Action"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}>
            <FontAwesomeIcon
              icon={faTrashCan}
              className="text-primary-red-500"
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-80 p-5 bg-white belegung-popover-content">
        <form
          className="flex flex-col gap-5 "
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faCircle}
                className={`size-4 text-(--dot-color)`}
                style={
                  {
                    '--dot-color': getColorsForMarkierung(
                      popoverRect.arbeitsbereich.markierung,
                    ).base,
                  } as React.CSSProperties & CSSVariables
                }
              />
              <div className="text-xl font-bold">
                {popoverRect.arbeitsbereich.name}
              </div>
            </div>
            <div className="text-sm text-neutrals-800">{`${wochentagLabel} • ${
              popoverRect.personenanzahlMax
            } ${t(
              'domain.dienstplan.mindestbelegung.visualEditor.popover.mitarbeitende',
            )}`}</div>
          </div>
          <div className="flex gap-4">
            <form.Field name="beginn">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1">
                    <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                      {t(
                        'domain.dienstplan.mindestbelegung.visualEditor.labels.von',
                      )}
                    </FieldLabel>
                    <TimeInput
                      autoFocus={false}
                      id={field.name}
                      defaultValue={formatHourDecimalToTimeString(
                        field.state.value,
                        offset,
                      )}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        const ende = transformTimeStringToDecimal(
                          e.target.value,
                          offset,
                        );
                        field.handleChange(ende);
                      }}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="ende">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1">
                    <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                      {t(
                        'domain.dienstplan.mindestbelegung.visualEditor.labels.bis',
                      )}
                    </FieldLabel>
                    <TimeInput
                      autoFocus={false}
                      id={field.name}
                      defaultValue={formatHourDecimalToTimeString(
                        field.state.value,
                        offset,
                      )}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        const value = transformTimeStringToDecimal(
                          e.target.value,
                          offset,
                        );
                        field.handleChange(value);
                      }}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            </form.Field>
          </div>
          <div>
            <form.Field name="anzahl">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                const showWarning =
                  field.state.value > maxMitarbeiterAnzahl.value &&
                  field.state.value <=
                    maxMitarbeiterAnzahl.value + MITARBEITER_THRESHOLD;

                return (
                  <Field
                    data-invalid={isInvalid}
                    className="flex flex-col gap-1">
                    <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                      {t(
                        'domain.dienstplan.mindestbelegung.visualEditor.labels.anzahlMitarbeitende',
                      )}
                    </FieldLabel>
                    <Input
                      autoFocus={false}
                      id={field.name}
                      defaultValue={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(parseInt(e.target.value));
                      }}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {showWarning && (
                      <FieldError
                        className="text-primary-orange-500"
                        errors={[
                          {
                            message: t(
                              'domain.dienstplan.mindestbelegung.visualEditor.popover.validationErrors.anzahlMitarbeitendeZuHoch',
                              {
                                count: field.state.value,
                                maxCount: maxMitarbeiterAnzahl.value,
                              },
                            ),
                          },
                        ]}
                      />
                    )}
                  </Field>
                );
              }}
            </form.Field>
          </div>
          <div className="flex items-center justify-end">
            <Button>Fertig</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
